import { useEffect, useState } from "react";
import { NavLink, useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  FileText,
  FileBarChart,
  LogOut,
  BadgeCheck,
  ShieldCheck,
  Sparkles,
  BookOpen,
  ClipboardCheck,
  GraduationCap,
  AlertTriangle,
  X,
  Settings2,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import Brand from "../components/site/Brand";
import InstallChip from "../components/pwa/InstallChip";

const OWNER_NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "app-nav-dashboard" },
  { to: "/app/competencies", label: "Competencies", icon: Sparkles, testid: "app-nav-competencies" },
  { to: "/app/team", label: "Team", icon: Users, testid: "app-nav-team" },
  { to: "/app/documents", label: "Documents", icon: FileText, testid: "app-nav-documents" },
  { to: "/app/learning", label: "Training modules", icon: BookOpen, testid: "app-nav-learning" },
  { to: "/app/learning-review", label: "Learning review", icon: ClipboardCheck, testid: "app-nav-learning-review" },
  { to: "/app/training", label: "Training", icon: BadgeCheck, testid: "app-nav-training" },
  { to: "/app/my-training", label: "My training", icon: GraduationCap, testid: "app-nav-my-training-owner" },
  { to: "/app/reports", label: "Reports", icon: FileBarChart, testid: "app-nav-reports" },
  { to: "/app/admin", label: "Administration", icon: Settings2, testid: "app-nav-admin" },
];

const STAFF_NAV = [
  { to: "/app/my-training", label: "My training", icon: GraduationCap, testid: "app-nav-my-training" },
  { to: "/app/my-certificates", label: "My certificates", icon: BadgeCheck, testid: "app-nav-my-certificates" },
];

export default function AppShell({ children }) {
  const { user, business, organisations, logout, switchOrg } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isDemo = business?.is_demo;
  const nav = user?.role === "staff" ? STAFF_NAV : OWNER_NAV;
  const hasMultiOrg = (organisations || []).length > 1;

  // Access-denied one-shot banner (populated by ProtectedRoute).
  const denied = params.get("denied") === "1";
  const deniedFrom = params.get("from") || "";
  const [showDenied, setShowDenied] = useState(denied);
  useEffect(() => {
    if (denied) setShowDenied(true);
  }, [denied]);
  function dismissDenied() {
    const next = new URLSearchParams(params);
    next.delete("denied");
    next.delete("from");
    setParams(next, { replace: true });
    setShowDenied(false);
  }

  function handleLogout() {
    logout();
    navigate("/");
  }

  return (
    <div className="min-h-screen bg-[#F6F5F0] text-slate-900 font-sans" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-64 bg-[#0B1220] text-slate-100 flex flex-col z-30 hidden md:flex min-h-0">
        <div className="px-6 py-6 border-b border-white/[0.08] shrink-0">
          <Brand />
        </div>

        <nav
          className="flex-1 min-h-0 overflow-y-auto px-3 py-5 space-y-1 [scrollbar-width:thin] [scrollbar-color:rgba(212,166,90,0.45)_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-[#D4A65A]/45 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-white/10 hover:[&::-webkit-scrollbar-thumb]:bg-[#D4A65A]/70 [&::-webkit-scrollbar-track]:bg-white/[0.04] [&::-webkit-scrollbar-track]:rounded-full"
          data-testid="app-sidebar-nav"
        >
          {nav.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-[13.5px] transition-colors ${
                  isActive
                    ? "bg-[#D4A65A]/15 text-[#F0D497] border border-[#D4A65A]/30"
                    : "text-slate-300 hover:bg-white/[0.05] hover:text-white border border-transparent"
                }`
              }
            >
              <Icon size={16} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-white/[0.08] shrink-0">
          <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4A65A] font-semibold">
              {business?.name || "Business"}
            </div>
            <div className="mt-1 text-[12.5px] text-slate-300 truncate">{user?.email}</div>
            {isDemo && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 text-[9.5px] uppercase tracking-[0.16em] text-emerald-300 font-semibold">
                <ShieldCheck size={10} /> Demo mode
              </div>
            )}
            {hasMultiOrg && (
              <div className="mt-3">
                <div className="text-[9.5px] uppercase tracking-[0.16em] text-slate-400 font-semibold mb-1">
                  Switch organisation
                </div>
                <select
                  value={business?.id || ""}
                  onChange={(e) => {
                    if (e.target.value && e.target.value !== business?.id) {
                      switchOrg(e.target.value);
                    }
                  }}
                  data-testid="app-org-switcher"
                  className="w-full rounded-md bg-[#0B1220] border border-white/[0.1] text-slate-100 text-[12px] px-2 py-1.5 focus:border-[#D4A65A] focus:outline-none focus:ring-2 focus:ring-[#D4A65A]/20"
                >
                  {(organisations || []).map((o) => (
                    <option key={o.id} value={o.id} className="text-slate-900">
                      {o.name}
                      {o.role ? ` (${o.role})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button
              onClick={handleLogout}
              data-testid="app-logout"
              className="mt-3 w-full inline-flex items-center gap-2 text-[12px] text-slate-300 hover:text-white transition-colors"
            >
              <LogOut size={12} />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 bg-[#0B1220] text-white px-4 py-3 flex items-center justify-between border-b border-white/10">
        <Brand />
        <div className="flex items-center gap-2">
          <InstallChip variant="deep" testid="app-install-chip" />
          <button
            onClick={handleLogout}
            data-testid="app-logout-mobile"
            className="text-[12px] text-slate-300 hover:text-white inline-flex items-center gap-1.5"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </header>

      {/* Mobile nav strip */}
      <nav className="md:hidden sticky top-[52px] z-20 bg-white border-b border-slate-200 overflow-x-auto">
        <div className="flex items-center gap-1 px-3 py-2">
          {nav.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] whitespace-nowrap transition-colors ${
                  isActive
                    ? "bg-[#0B1220] text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`
              }
            >
              <Icon size={13} /> {label}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Main */}
      <main className="md:pl-64">
        <div className="max-w-6xl mx-auto px-5 md:px-10 py-8 md:py-10">
          {showDenied && (
            <div
              data-testid="access-denied-banner"
              className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
            >
              <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1 text-[13px] text-amber-900 leading-relaxed">
                You don't have access to
                {deniedFrom ? <> <span className="font-mono text-[12.5px]">{deniedFrom}</span></> : " that area"}
                {" "}with your current account.
                {user?.role === "staff" && " Ask your administrator if you need access."}
              </div>
              <button
                onClick={dismissDenied}
                data-testid="access-denied-dismiss"
                className="text-amber-800 hover:text-amber-950"
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
